import { Check, X, AlertTriangle, Clock, type LucideIcon } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { cn } from '@reflex/ui/lib/utils';
import { WEBHOOK_HEALTH_BADGE } from '@/lib/status-colors';
import type { WebhookSourceSummary } from '@/lib/types';

const HEALTH_ICON: Record<string, LucideIcon> = {
  healthy: Check,
  degraded: AlertTriangle,
  failing: X,
  no_activity: Clock,
};

function deriveHealth(source: WebhookSourceSummary): string {
  if (source.lastReceivedAt === null) return 'no_activity';
  const errorRate = source.totalReceived > 0 ? source.totalErrors / source.totalReceived : 0;
  if (source.lastError && errorRate > 0.5) return 'failing';
  if (source.totalErrors > 0) return 'degraded';
  return 'healthy';
}

export function WebhookStatusBadge({ source }: { source: WebhookSourceSummary }) {
  const health = deriveHealth(source);
  const cfg = WEBHOOK_HEALTH_BADGE[health];
  const Icon = HEALTH_ICON[health];

  return (
    <Badge
      variant="outline"
      className={cn('text-3xs gap-1 px-1.5 py-0 font-medium tracking-wide', cfg?.className)}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {cfg?.label ?? health}
    </Badge>
  );
}

export function StatusCodeBadge({ code }: { code: number }) {
  const className =
    code >= 400
      ? 'bg-status-error/15 text-status-error border-status-error/30'
      : 'bg-status-success/15 text-status-success border-status-success/30';

  return (
    <Badge variant="outline" className={cn('text-3xs font-mono px-1.5 py-0', className)}>
      {code}
    </Badge>
  );
}
